import { useMutation, useQueryClient } from 'react-query'

import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  legalEntityKeys,
  legalEntityRepository,
} from '@webapp/platform/legal-entities/data-access/repositories/LegalEntityRepository'

interface DeactivateLegalEntityParams {
  uuid: string
}

/**
 * Deactivates (soft-deletes) an active LegalEntity and invalidates the cached
 * LegalEntity list. That single query feeds both the Active/Inactive list tabs
 * and the details view (`useQueryLegalEntity` derives a single entity from the
 * list), so invalidating it refreshes both surfaces — the row moves to the
 * Inactive tab and the detail badge flips to Inactive.
 */
export const useMutateDeactivateLegalEntity = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<void, RepoError, DeactivateLegalEntityParams>({
    mutationFn: ({ uuid }: DeactivateLegalEntityParams) => {
      return legalEntityRepository.deactivate(uuid)
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.list })
    },
  })

  return {
    deactivate: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  }
}
